// client/src/dnd/emitter_tool/runtime/Runtime_Parts/SimpleEmitter.js
import * as PIXI from "pixi.js";
import { sampleSpawn } from "../../engine/engine_parts/emitter/spawnSampler";
import PulseFollower from "../../engine/engine_parts/emitter/PulseFollower";
import CollisionManager from "../../engine/CollisionManager.js";
import {
  blendModeOf,
  clamp,
  createTextureKeyResolver,
  DEG,
  lerp,
  nowSec,
  resolveSpriteSource,
  safeSig,
  toNum,
} from "./utils.js";

function isFullContainerShapeKey(k) {
  const s = String(k || "").toLowerCase();
  return (
    s === "random" ||
    s === "full" ||
    s === "fullcontainer" ||
    s === "container" ||
    s === "screen" ||
    s === "image" ||
    s === "canvas"
  );
}

function rectEdgePointFromCentre(rect, centre, pos) {
  const dirx = pos.x - centre.x;
  const diry = pos.y - centre.y;
  const EPS = 1e-6;
  const vx = Math.abs(dirx) < EPS ? (dirx >= 0 ? EPS : -EPS) : dirx;
  const vy = Math.abs(diry) < EPS ? (diry >= 0 ? EPS : -EPS) : diry;

  const left = rect.x;
  const right = rect.x + rect.width;
  const top = rect.y;
  const bottom = rect.y + rect.height;

  const tx1 = (left - centre.x) / vx;
  const tx2 = (right - centre.x) / vx;
  const ty1 = (top - centre.y) / vy;
  const ty2 = (bottom - centre.y) / vy;

  const candidates = [tx1, tx2, ty1, ty2].filter((t) => t > 0);
  const t = candidates.length ? Math.min(...candidates) : 0;
  return { x: centre.x + vx * t, y: centre.y + vy * t };
}

export function createCollisionManager(imageRect, helpers, initialSettings = {}) {
  return new CollisionManager({
    getWalls: () => (typeof helpers?.getWalls === "function" ? helpers.getWalls() : { polygons: [] }),
    getToken: () => {
      if (typeof helpers?.getToken === "function") {
        const token = helpers.getToken();
        return Array.isArray(token) ? token : (token ? [token] : []);
      }
      if (Array.isArray(window.__PA_TOKENS)) return window.__PA_TOKENS;
      return [];
    },
    settings: initialSettings || {},
    dispatch: (name, detail) => {
      try {
        window.dispatchEvent(new CustomEvent(name, { detail }));
      } catch {}
    },
    space: "image",
    boundsRect: imageRect,
  });
}

function anchorFromShape(_emitterShape, origin) {
  return { x: origin.x, y: origin.y };
}

function createSelectionResolver(textures, selection) {
  const textureResolver = createTextureKeyResolver(textures);
  const sel = selection || { mode: "single", single: null, multi: [], anim: [], animFps: 12 };
  const mode = String(sel.mode || "").toLowerCase();

  const animKeys = mode === "anim" ? textureResolver.resolveList(sel.anim) : [];
  const multiKeys = mode === "multi" ? textureResolver.resolveList(sel.multi) : [];
  const singleKey = mode === "single" && sel.single ? textureResolver.resolveList([sel.single])[0] || null : null;
  const animFps = Math.max(1, Math.min(120, Math.floor(sel.animFps || 12)));
  const animWillRun = animKeys.length >= 2;

  function pickTexture(dt, spriteSource) {
    if (animWillRun) {
      const first = animKeys[0];
      return textures[first] || PIXI.Texture.WHITE;
    }
    if (singleKey && textures[singleKey]) return textures[singleKey];
    if (multiKeys.length > 0) {
      const idx = Math.floor(Math.random() * multiKeys.length);
      return textures[multiKeys[idx]] || PIXI.Texture.WHITE;
    }
    return spriteSource.next(dt || 0) || PIXI.Texture.WHITE;
  }

  function applyAnimState(sprite) {
    if (!animWillRun) return;
    sprite.__animKeys = animKeys;
    sprite.__animFps = animFps;
    sprite.__animLoop = true;
    sprite.__animTime = 0;
    sprite.__animIndex = 0;
    sprite.__setTex = (k) => {
      const texture = textures[k];
      if (texture) sprite.texture = texture;
    };
  }

  return {
    animWillRun,
    pickTexture,
    applyAnimState,
  };
}

function stepSpriteAnimation(sprite, dt) {
  if (!sprite?.__animKeys || sprite.__animKeys.length < 2) return;
  const frameDur = 1 / Math.max(1, sprite.__animFps || 12);
  sprite.__animTime = (sprite.__animTime || 0) + dt;
  while (sprite.__animTime >= frameDur) {
    sprite.__animTime -= frameDur;
    let next = (sprite.__animIndex || 0) + 1;
    if (next >= sprite.__animKeys.length) next = sprite.__animLoop ? 0 : sprite.__animKeys.length - 1;
    if (next !== sprite.__animIndex) {
      sprite.__animIndex = next;
      try {
        sprite.__setTex?.(sprite.__animKeys[next]);
      } catch {}
    }
  }
}

function readPulseState(shapeSettings, getNowSec) {
  const pulseEnabled = !!shapeSettings.pulseEnabled;
  const pulsePingPong = !!shapeSettings.pulsePingPong;
  const pulseMinPct = Math.max(0, Math.min(0.99, Number(shapeSettings.pulseMinPct ?? 1)));
  const pulseExpandS = Math.max(1e-4, Number(shapeSettings.pulseExpandTimeS ?? 1.5));
  const pulseHoldS = Math.max(0, Number(shapeSettings.pulseHoldAtMaxS ?? 0));
  const pulseDelayS = Math.max(0, Number(shapeSettings.pulseDelayAfterEmptyS ?? 0));
  const pulsePhaseOffS = Number(shapeSettings.pulsePhaseOffsetS ?? 0) || 0;
  let pulseTimeBaseSec = getNowSec();

  function reset() {
    pulseTimeBaseSec = getNowSec();
  }

  function scaleNow() {
    if (!pulseEnabled) return 1;
    const t = getNowSec() - pulseTimeBaseSec - pulsePhaseOffS;
    let k;
    if (pulsePingPong) {
      const up = pulseExpandS;
      const cycle = up * 2;
      const p = ((t % cycle) + cycle) % cycle;
      k = p < up ? p / up : 1 - (p - up) / up;
    } else {
      const up = pulseExpandS;
      const hold = pulseHoldS;
      const wait = pulseDelayS;
      const total = up + hold + wait || up;
      const p = ((t % total) + total) % total;
      k = p < up ? p / up : p < up + hold ? 1 : 0;
    }
    return Math.max(1e-6, pulseMinPct + (1 - pulseMinPct) * k);
  }

  return {
    reset,
    scaleNow,
  };
}

function buildSettingsSnapshot(effectSettings, shapeSettings, pxPerMeter) {
  const speedMin = Math.max(0, toNum(effectSettings.speedMin, 1));
  const speedMax = Math.max(speedMin, toNum(effectSettings.speedMax, 3));
  const lifeMinS = Math.max(0, toNum(effectSettings.lifeMinS ?? 1));
  const lifeMaxS = Math.max(lifeMinS, toNum(effectSettings.lifeMaxS ?? 2.5));
  const alphaStart = clamp(toNum(effectSettings.alphaStart ?? 1, 1), 0, 1);
  const alphaEnd = clamp(toNum(effectSettings.alphaEnd ?? 0, 0), 0, 1);
  const scaleStart = Math.max(0, toNum(effectSettings.scaleStart ?? 1, 1));
  const scaleEnd = Math.max(0, toNum(effectSettings.scaleEnd ?? 1, 1));
  const emissionMode = String(shapeSettings.emissionMode || effectSettings.emissionMode || "area");
  const edgeBehavior = String(shapeSettings.edgeBehavior || effectSettings.edgeBehavior || "out");
  const rotateEnabled = !!effectSettings.rotateEnabled;
  const rotateDirection = effectSettings.rotateDirection === "cw" ? 1 : -1;
  const rotateAbsDps = Math.max(0, toNum(effectSettings.rotateSpeedAbsDps, 0));
  const rotateSpeedDegPerS = Number.isFinite(effectSettings.rotateSpeedDegPerS)
    ? Number(effectSettings.rotateSpeedDegPerS)
    : rotateDirection * rotateAbsDps;
  const rotationAnimEnabled = !!shapeSettings.rotationAnimEnabled && Math.max(0, Number(shapeSettings.rotationAnimSpeedDegS || 0)) > 0;
  const rotationAnimDir = shapeSettings.rotationAnimDir === "cw" ? 1 : -1;
  const rotationAnimSpeedDegS = Math.max(0, Number(shapeSettings.rotationAnimSpeedDegS || 0));
  const rotationAffectsParticles = shapeSettings.rotationAffectsParticles !== false;

  return {
    pps: toNum(effectSettings.pps, 100),
    maxAlive: Math.max(0, Math.floor(toNum(effectSettings.maxAlive, 1000))),
    speedMin,
    speedMax,
    lifeMinS,
    lifeMaxS,
    alphaStart,
    alphaEnd,
    scaleStart,
    scaleEnd,
    blendMode: blendModeOf(effectSettings.blendMode),
    emissionMode,
    edgeBehavior,
    rotateEnabled,
    rotateSpeedRadS: rotateSpeedDegPerS * DEG,
    rotationAnimEnabled,
    rotationAnimDir,
    rotationAnimSpeedDegS,
    rotationAffectsParticles,
    spawnFromOrigin: !!shapeSettings.spawnFromOrigin,
    lifespanMode: String(effectSettings.lifespanMode || "time").toLowerCase(),
    rangePx: Math.max(0, Number(effectSettings.rangeMeters || 0)) * pxPerMeter,
    lingerES: Math.max(0, Number(effectSettings.gravityLingerS || 0)),
    particleRadiusPx: Number(effectSettings.particleRadiusPx) || 10,
    useWalls: effectSettings.useWalls == null ? true : !!effectSettings.useWalls,
    wallResponse: effectSettings.wallResponse || "bounce",
    restitution: effectSettings.restitution == null ? 1 : Number(effectSettings.restitution),
    useTokenCollision: !!effectSettings.useTokenCollision,
  };
}

export function createSimpleEmitter(app, mount, opts) {
  const {
    textures,
    pxPerMeter = 80,
    emitterShape = "dot",
    shapeSettings = {},
    effectSettings = {},
    spriteSource,
    imageRect = { x: 0, y: 0, width: app?.renderer?.width ?? 0, height: app?.renderer?.height ?? 0 },
    selection = null,
    collision = null,
    glowManager = null,
    gravityManager = null,
    onParticleDeath = null,
    onAllParticlesDead = null,
  } = opts || {};

  if (!spriteSource || typeof spriteSource.next !== "function") {
    throw new Error("[createSimpleEmitter] spriteSource.next(dt) required");
  }

  const mountContainer = mount || (app?.stage || app);
  if (!mountContainer || typeof mountContainer.addChild !== "function") {
    throw new Error("[createSimpleEmitter] No mount container and no stage/root");
  }

  const container = new PIXI.Container();
  container.sortableChildren = true;
  container.zIndex = 100;
  mountContainer.addChild(container);

  const particles = [];
  const selectionResolver = createSelectionResolver(textures, selection);

  const getNowSec = () => {
    if (app?.ticker && typeof app.ticker.lastTime === "number") return app.ticker.lastTime / 1000;
    return nowSec();
  };

  const pulse = readPulseState(shapeSettings, getNowSec);

  let origin = {
    x: (imageRect.x || 0) + (imageRect.width || 0) * 0.5,
    y: (imageRect.y || 0) + (imageRect.height || 0) * 0.5,
  };

  let spawnAcc = 0;
  let spawnDtSince = 0;
  let spawning = true;
  let lastSpawnedPos = null;
  let rotAnimOffsetDeg = 0;
  let rotTimeBaseSec = getNowSec();
  let lastGravitySig = "";
  let lastCollisionSig = "";
  let settingsSnapshot = buildSettingsSnapshot(effectSettings, shapeSettings, pxPerMeter);

  function syncSettingsCache() {
    settingsSnapshot = buildSettingsSnapshot(effectSettings, shapeSettings, pxPerMeter);
  }

  function getHeadPosition() {
    if (particles.length > 0) {
      const p = particles[particles.length - 1];
      if (p && Number.isFinite(p.cx) && Number.isFinite(p.cy)) return { x: p.cx, y: p.cy };
    }
    if (lastSpawnedPos) return { ...lastSpawnedPos };
    return { ...origin };
  }

  function setOrigin(pos) {
    if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
      origin = { x: pos.x, y: pos.y };
    }
  }

  function setRotationDeg(deg) {
    if (Number.isFinite(deg)) {
      shapeSettings.rotationDeg = deg;
    }
  }

  function killParticle(i, reason) {
    const p = particles[i];
    const sprite = p?.s;
    if (!p || !sprite) {
      particles.splice(i, 1);
      return;
    }

    const deathX = p.cx;
    const deathY = p.cy;

    try {
      glowManager?.onParticleKill?.(sprite);
    } catch {}
    try {
      container.removeChild(sprite);
    } catch {}
    try {
      sprite.destroy?.();
    } catch {}

    if (typeof onParticleDeath === "function") {
      try {
        onParticleDeath({ x: deathX, y: deathY, reason });
      } catch {}
    }

    particles.splice(i, 1);

    if (particles.length === 0 && typeof onAllParticlesDead === "function") {
      try {
        onAllParticlesDead({ x: deathX, y: deathY, reason: reason || "life_end" });
      } catch {}
    }
  }

  function clearParticles() {
    for (let i = particles.length - 1; i >= 0; i--) {
      killParticle(i, "clear");
    }
  }

  function reset(opts = {}) {
    spawnAcc = 0;
    spawnDtSince = 0;
    rotAnimOffsetDeg = 0;
    rotTimeBaseSec = getNowSec();
    pulse.reset();
    lastSpawnedPos = null;
    lastGravitySig = "";
    lastCollisionSig = "";
    try {
      spriteSource.reset?.();
    } catch {}
    if (opts.clearParticles) clearParticles();
  }

  function syncGravity() {
    if (!gravityManager) return;
    const sig = safeSig({
      emitterShape,
      pxPerMeter,
      imageRect,
      shapeSettings,
      effectSettings,
      origin,
    });
    if (sig === lastGravitySig) return;
    lastGravitySig = sig;
    try {
      gravityManager.updateSettings(effectSettings);
      gravityManager.configure({
        emitterShape,
        shapeSettings,
        effectSettings,
        origin,
        pxPerMeter,
        imageRect,
      });
    } catch {}
  }

  function syncCollision() {
    if (!collision) return;
    const sig = safeSig({
      useWalls: settingsSnapshot.useWalls,
      wallResponse: settingsSnapshot.wallResponse,
      restitution: settingsSnapshot.restitution,
      particleRadiusPx: settingsSnapshot.particleRadiusPx,
      useTokenCollision: settingsSnapshot.useTokenCollision,
    });
    if (sig === lastCollisionSig) return;
    lastCollisionSig = sig;
    try {
      collision.updateSettings(effectSettings);
    } catch {}
  }

  function spawnOne(dt) {
    if (!spawning) return;
    if (particles.length >= settingsSnapshot.maxAlive) return;

    const sprite = new PIXI.Sprite(selectionResolver.pickTexture(dt, spriteSource) || PIXI.Texture.WHITE);
    sprite.anchor.set(0.5);
    sprite.blendMode = settingsSnapshot.blendMode;
    sprite.rotation = sprite.rotation || 0;
    selectionResolver.applyAnimState(sprite);

    const pulseScale = pulse.scaleNow();
    const baseRotRad =
      (Number(shapeSettings.rotationDeg || 0) +
        (settingsSnapshot.rotationAnimEnabled ? rotAnimOffsetDeg : 0)) *
      DEG;

    let cx;
    let cy;
    let ang;

    if (settingsSnapshot.spawnFromOrigin) {
      const anchor = anchorFromShape(emitterShape, origin, shapeSettings, pxPerMeter);
      cx = anchor.x;
      cy = anchor.y;

      const shape = String(emitterShape || "").toLowerCase();
      if (shape === "cone") {
        const half = clamp(+shapeSettings.spreadDeg || 30, 0, 180) * DEG * 0.5;
        ang = baseRotRad + (Math.random() * 2 - 1) * half;
      } else if (shape === "line" || shape === "beam") {
        ang = baseRotRad;
      } else if (settingsSnapshot.emissionMode === "edge" && settingsSnapshot.edgeBehavior === "pull") {
        ang = baseRotRad;
      } else {
        ang = Math.random() * Math.PI * 2;
      }
    } else {
      const ps = sampleSpawn({
        shape: emitterShape,
        shapeSettings,
        emissionMode: settingsSnapshot.emissionMode,
        edgeBehavior: settingsSnapshot.edgeBehavior,
        addRotDeg: settingsSnapshot.rotationAnimEnabled ? rotAnimOffsetDeg : 0,
        worldUnitsToPixels: (m) => m * pxPerMeter * pulseScale,
        origin,
      });

      cx = ps.x;
      cy = ps.y;

      if (String(emitterShape).toLowerCase() === "circle" && settingsSnapshot.emissionMode === "edge") {
        const Rpx = Math.max(0, Number(shapeSettings.radius || 0)) * pxPerMeter * pulseScale;
        const dx = cx - origin.x;
        const dy = cy - origin.y;
        const len = Math.hypot(dx, dy) || 1;
        cx = origin.x + (dx / len) * Rpx;
        cy = origin.y + (dy / len) * Rpx;
      }

      const shapeKey = String(emitterShape || "").toLowerCase();
      if (shapeKey === "dot") {
        ang = baseRotRad;
      } else if (shapeKey === "random") {
        ang = Math.random() * Math.PI * 2;
      } else if (shapeKey === "cone") {
        const half = clamp(+shapeSettings.spreadDeg || 30, 0, 180) * DEG * 0.5;
        ang = baseRotRad + (Math.random() * 2 - 1) * half;
      } else if (settingsSnapshot.emissionMode === "edge") {
        const normalX = ps.normal?.x ?? 0;
        const normalY = ps.normal?.y ?? 0;
        const tangentX = ps.tangent?.x ?? 0;
        const tangentY = ps.tangent?.y ?? 0;
        if (settingsSnapshot.edgeBehavior === "pull") ang = Math.atan2(cy - origin.y, cx - origin.x) + Math.PI;
        else if (settingsSnapshot.edgeBehavior === "cling") {
          ang = settingsSnapshot.rotationAnimEnabled ? null : Math.atan2(tangentY, tangentX);
        } else {
          ang = Math.atan2(normalY, normalX);
        }
      } else {
        ang = Math.atan2(cy - origin.y, cx - origin.x);
      }
    }

    const gravityEnabled = !!gravityManager?.isEnabled?.();
    const gravitySettings = gravityEnabled ? gravityManager.getSettings?.() : null;
    const gravitySpeed = Number(gravitySettings?.speed);
    let inwardStartDist = null;

    if (
      gravityEnabled &&
      Number.isFinite(gravitySpeed) &&
      gravitySpeed !== 0 &&
      isFullContainerShapeKey(emitterShape) &&
      !settingsSnapshot.spawnFromOrigin
    ) {
      const rect = {
        x: imageRect.x || 0,
        y: imageRect.y || 0,
        width: imageRect.width || 0,
        height: imageRect.height || 0,
      };
      const centre = { x: origin.x, y: origin.y };
      const edge = rectEdgePointFromCentre(rect, centre, { x: cx, y: cy });
      cx = edge.x;
      cy = edge.y;
      if (gravitySpeed < 0) inwardStartDist = Math.hypot(cx - centre.x, cy - centre.y);
      if (gravitySpeed > 0) ang = Math.random() * Math.PI * 2;
    }

    lastSpawnedPos = { x: cx, y: cy };

    const startScale = settingsSnapshot.scaleStart <= 0 ? 0.0001 : settingsSnapshot.scaleStart;
    sprite.position.set(cx, cy);
    sprite.scale.set(startScale);
    sprite.alpha = settingsSnapshot.alphaStart;
    sprite.__pulseMeta = {
      birthScale: pulseScale,
      enabled: shapeSettings.pulseMovesParticles !== false,
    };

    if (shapeSettings.pulseMovesParticles !== false) {
      try {
        PulseFollower.onSpawn(sprite, origin.x, origin.y, pulseScale);
      } catch {}
    }

    const speedPx = lerp(settingsSnapshot.speedMin, settingsSnapshot.speedMax, Math.random()) * pxPerMeter;
    const vx = ang == null ? 0 : Math.cos(ang) * speedPx;
    const vy = ang == null ? 0 : Math.sin(ang) * speedPx;
    const life = lerp(settingsSnapshot.lifeMinS, settingsSnapshot.lifeMaxS, Math.random());
    const rigidRotate =
      settingsSnapshot.emissionMode === "edge" &&
      settingsSnapshot.edgeBehavior === "cling" &&
      settingsSnapshot.rotationAnimEnabled &&
      settingsSnapshot.rotationAffectsParticles;

    container.addChild(sprite);
    try {
      glowManager?.onParticleSpawn?.(sprite);
    } catch {}

    const particle = {
      s: sprite,
      t: 0,
      life,
      cx,
      cy,
      vx,
      vy,
      radius: settingsSnapshot.particleRadiusPx,
      rigidRotate,
      __dist: 0,
      __prevDist: Math.hypot(cx - origin.x, cy - origin.y),
      __stopR: null,
      __gravFrozen: false,
      __freezeX: 0,
      __freezeY: 0,
      __lingerRemainS: 0,
    };

    Object.defineProperties(particle, {
      x: { get() { return this.cx; }, set(v) { this.cx = v; } },
      y: { get() { return this.cy; }, set(v) { this.cy = v; } },
    });

    if (
      gravityEnabled &&
      Number.isFinite(gravitySpeed) &&
      gravitySpeed < 0 &&
      inwardStartDist != null &&
      inwardStartDist > 0
    ) {
      const minFrac = 0.1;
      const maxFrac = 0.9;
      const frac = minFrac + Math.random() * (maxFrac - minFrac);
      particle.__stopR = Math.max(1, inwardStartDist * frac);
    }

    particles.push(particle);
  }

  function updateParticle(p, dt, dAng, collisionEnabled) {
    const sprite = p.s;

    if (p.__gravFrozen) {
      p.cx = p.__freezeX;
      p.cy = p.__freezeY;
      p.vx = 0;
      p.vy = 0;
      p.__lingerRemainS = Math.max(0, (p.__lingerRemainS || 0) - dt);
      if (p.__lingerRemainS <= 0) return "linger_end";
      sprite.x = p.cx;
      sprite.y = p.cy;
      sprite.alpha = settingsSnapshot.alphaStart;
      stepSpriteAnimation(sprite, dt);
      try {
        glowManager?.onParticleStep?.(sprite, dt, sprite.alpha);
      } catch {}
      return null;
    }

    p.t += dt;
    if (p.__dead) return "flag_dead";

    const advancedByGravity = !!p.__gravMoved;
    if (advancedByGravity) {
      p.vx = 0;
      p.vy = 0;
      p.__gravMoved = false;
    }

    const prevX = p.cx;
    const prevY = p.cy;

    if (!advancedByGravity) {
      if (collisionEnabled) {
        const phys = { x: p.cx, y: p.cy, vx: p.vx, vy: p.vy, radius: p.radius };
        const killed = !!collision.handleParticle(phys, dt);
        p.cx = phys.x;
        p.cy = phys.y;
        p.vx = phys.vx;
        p.vy = phys.vy;
        if (killed) return "collision_kill";
      } else {
        p.cx += p.vx * dt;
        p.cy += p.vy * dt;
      }
    }

    const moved = Math.hypot(p.cx - prevX, p.cy - prevY);
    if (moved > 0) p.__dist += moved;

    if (!advancedByGravity && p.rigidRotate && dAng !== 0) {
      const dx = p.cx - origin.x;
      const dy = p.cy - origin.y;
      const c = Math.cos(dAng);
      const s = Math.sin(dAng);
      p.cx = origin.x + (dx * c - dy * s);
      p.cy = origin.y + (dx * s + dy * c);
    }

    const distNow = Math.hypot(p.cx - origin.x, p.cy - origin.y);

    if (p.__stopR != null) {
      const reachedTarget = distNow <= p.__stopR;
      const movedOutward = distNow > p.__prevDist + 1e-3;
      if (reachedTarget || movedOutward) {
        if (settingsSnapshot.lingerES > 0) {
          p.__gravFrozen = true;
          p.__freezeX = p.cx;
          p.__freezeY = p.cy;
          p.vx = 0;
          p.vy = 0;
          p.__lingerRemainS = settingsSnapshot.lingerES;
          p.__stopR = null;
          sprite.x = p.cx;
          sprite.y = p.cy;
          return null;
        }
        return reachedTarget ? "hit_stop_radius" : "wrong_way";
      }
    }

    sprite.x = p.cx;
    sprite.y = p.cy;

    if (settingsSnapshot.rotateEnabled && settingsSnapshot.rotateSpeedRadS !== 0 && !advancedByGravity) {
      sprite.rotation = (sprite.rotation || 0) + settingsSnapshot.rotateSpeedRadS * dt;
    }

    let lifeK = 0;

    if (settingsSnapshot.lifespanMode === "range" && settingsSnapshot.rangePx > 0) {
      lifeK = clamp(p.__dist / settingsSnapshot.rangePx, 0, 1);
      if (p.__dist >= settingsSnapshot.rangePx) return "range_end";
    } else if (settingsSnapshot.lifespanMode === "to_target") {
      lifeK = 0;
    } else if (p.__stopR == null) {
      lifeK = clamp(p.t / p.life, 0, 1);
      if (lifeK >= 1) return "life_end";
    }

    sprite.alpha = lerp(settingsSnapshot.alphaStart, settingsSnapshot.alphaEnd, lifeK);
    const scale = lerp(settingsSnapshot.scaleStart, settingsSnapshot.scaleEnd, lifeK);
    sprite.scale.set(scale <= 0 ? 0.0001 : scale);

    stepSpriteAnimation(sprite, dt);

    try {
      glowManager?.onParticleStep?.(sprite, dt, sprite.alpha);
    } catch {}

    p.__prevDist = distNow;
    return null;
  }

  function update(dt) {
    syncSettingsCache();

    if (settingsSnapshot.rotationAnimEnabled && settingsSnapshot.rotationAnimSpeedDegS > 0) {
      const tSec = getNowSec() - rotTimeBaseSec;
      rotAnimOffsetDeg = settingsSnapshot.rotationAnimDir * settingsSnapshot.rotationAnimSpeedDegS * tSec;
    } else {
      rotAnimOffsetDeg = 0;
    }

    syncGravity();
    if (gravityManager?.update) {
      try {
        gravityManager.update(particles, dt);
      } catch {}
    }

    spawnAcc += settingsSnapshot.pps * dt;
    spawnDtSince += dt;
    const capacity = Math.max(0, settingsSnapshot.maxAlive - particles.length);
    const toSpawn = Math.min(capacity, Math.floor(spawnAcc));
    spawnAcc -= toSpawn;

    for (let i = 0; i < toSpawn; i++) {
      spawnOne(spawnDtSince);
      spawnDtSince = 0;
    }

    const collisionsEnabled = !!collision && (settingsSnapshot.useWalls || settingsSnapshot.useTokenCollision);
    if (collisionsEnabled) syncCollision();

    const omega =
      settingsSnapshot.rotationAnimEnabled &&
      settingsSnapshot.rotationAffectsParticles &&
      settingsSnapshot.rotationAnimSpeedDegS > 0
        ? settingsSnapshot.rotationAnimDir * settingsSnapshot.rotationAnimSpeedDegS * DEG
        : 0;
    const dAng = omega * dt;

    for (let i = particles.length - 1; i >= 0; i--) {
      const reason = updateParticle(particles[i], dt, dAng, collisionsEnabled);
      if (reason) killParticle(i, reason);
    }
  }

  function destroy() {
    clearParticles();
    try {
      container.parent?.removeChild(container);
    } catch {}
    try {
      container.destroy({ children: true });
    } catch {}
  }

  return {
    container,
    setOrigin,
    getOrigin: () => ({ ...origin }),
    setRotationDeg,
    update,
    destroy,
    setSpawning: (b) => {
      spawning = !!b;
    },
    isSpawning: () => !!spawning,
    hasLiveParticles: () => particles.length > 0,
    clearParticles,
    reset,
    getEmitterShape: () => emitterShape,
    getShapeSettings: () => shapeSettings,
    getHeadPosition,
  };
}

export function makeSpriteSource({ spriteManager, textures }) {
  return resolveSpriteSource({ spriteManager, textures });
}

export default { createSimpleEmitter, createCollisionManager, makeSpriteSource };
